/** Modo claro/oscuro del Portal del alumno y de los programas digitales.
 *  Por defecto SIEMPRE oscuro; el claro solo se activa si la persona lo elige con el interruptor sol/luna
 *  (no se sigue la preferencia del teléfono). La elección se guarda en este dispositivo (localStorage, con
 *  try/catch porque puede estar bloqueado) y `public/theme-init.js` la aplica antes del primer render.
 *  El atributo `data-portal-theme` solo existe mientras hay una pantalla del Portal o de un programa
 *  montada: staff, panel y sitio público nunca lo llevan. */
import { useCallback, useEffect, useSyncExternalStore } from "react";

export type PortalTheme = "dark" | "light";
const KEY = "cp_portal_theme";
const META_OSCURO = "#000000";
const META_CLARO = "#f4f6f9";
const listeners = new Set<() => void>();

export function getPortalTheme(): PortalTheme {
  try { return localStorage.getItem(KEY) === "light" ? "light" : "dark"; } catch { return "dark"; }
}

function paintMeta(t: PortalTheme) {
  const m = document.querySelector('meta[name="theme-color"]');
  if (m) m.setAttribute("content", t === "light" ? META_CLARO : META_OSCURO);
}

/** Pone o quita el atributo y el color de la barra del navegador. */
export function applyPortalTheme(t: PortalTheme | null) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  if (t === "light") root.setAttribute("data-portal-theme", "light");
  else root.removeAttribute("data-portal-theme");
  paintMeta(t === "light" ? "light" : "dark");
}

export function setPortalTheme(t: PortalTheme) {
  try { localStorage.setItem(KEY, t); } catch { /* sin almacenamiento: vale solo para esta visita */ }
  memoria = t;
  applyPortalTheme(t);
  listeners.forEach((l) => l());
}

let memoria: PortalTheme | null = null;
function snapshot(): PortalTheme { return memoria ?? getPortalTheme(); }
function subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; }

/** Llamar en la pantalla raíz del Portal o del programa (aplicar=true: pone y quita el atributo) y en las
 *  pantallas internas que solo necesitan leer o cambiar el modo (aplicar=false). `enabled=false` (vista previa
 *  del staff dentro del panel) lo deja siempre oscuro. */
export function usePortalTheme(enabled = true, aplicar = true) {
  const theme = useSyncExternalStore(subscribe, snapshot, () => "dark" as PortalTheme);
  useEffect(() => {
    if (!enabled || !aplicar) return;
    applyPortalTheme(snapshot());
    return () => applyPortalTheme(null); // al salir hacia una pantalla pública todo vuelve a oscuro
  }, [enabled, aplicar]);
  const toggle = useCallback(() => setPortalTheme(snapshot() === "light" ? "dark" : "light"), []);
  return { theme: enabled ? theme : ("dark" as PortalTheme), toggle };
}
